import React, { useEffect, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Card,
  CardContent,
  Box, 
  Button,
  Stack,
  Tooltip,
  Chip,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as ProgramIcon,
  MoreVert as ActionsIcon,
  CheckBox,
  StoreMallDirectoryTwoTone,
  IndeterminateCheckBoxRounded
} from '@mui/icons-material';
import AddIcon from "@mui/icons-material/Add";
import { AddRounded } from "@mui/icons-material";
import AddProgram from "../Dialogs/AddProgram";
import { useDispatch, useSelector } from "react-redux";
import { setSnackError } from "../../Reducers/snackErrors";
import API from "../../api/api";
import { setSuccessSnack } from "../../Reducers/snackSuccess";
import usePermissions from "../../Reducers/usePermissions";
import DeleteConfirmationDialog from "../Dialogs/ConfirmDelete";
import AddSurvey from "../Dialogs/AddSurvey";

const SurveyList = (props) => {
  // Sample data for Program_root
  const role = useSelector((state) => state.role);
  const {hasPermission} = usePermissions();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [trigger, setTrigger] = useState(false);
  const [initialData, setInitialData] = useState({})

  const handleUpdate = (data) => {
    setTrigger(true);
    setInitialData(data);
  }

  const openAddProgram = () => {
    setTrigger(true);
  }

  const [confirmDelete, setConfirmDelete] = useState({id: null, open: false, confirm: false});


  const confirmHandleDelete = async (id) => {
    if (!id) { return; }
    setConfirmDelete({id: id, open: true, confirm: false});
  }

  const closeConfirmHandleDelete = () => {
    setInitialData({})
    setConfirmDelete({id: null, open: false, confirm: false});
  }


  const handleDelete = async () => {
    setLoading(true);
    try {
      const id = confirmDelete.id;
      const response = await API.post('/delete_survey', {id});
      if (response.data.status === 'Removed') {
          dispatch(setSuccessSnack({title: 'Success', body:'Removed survey'}))
        return;
      }
    }
    catch(error) {
        console.log(error);
        if (error.isAuthError || error.isForbidden) {
          return;
        } 
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to add Program.'
          }));
        }
        return;
      }finally {
        closeConfirmHandleDelete();
        setLoading(false);
        getData()

      } 
  }

  useEffect(() => {
    getData();
  }, [])

  const getData = async () => {
    try {
      setLoading(true);
      const response = await API.get('/get_surveys')
      if (!response.data.data) {
        setSurveys([]);
        return;
      }
      if (response.data.data) {
        let questions = response.data.questions;
        let surveys = response.data.data;
        let surveysParsed = {}
        for (const item of surveys) {
            surveysParsed[item.id] = { ...item, questions: [] }
        }
        for (const q of questions) {
            if (surveysParsed[q.survey_id]){
                const qref = surveysParsed[q.survey_id].questions;
                qref.push(q)
                surveysParsed[q.survey_id] = {...surveysParsed[q.survey_id], questions: qref}
            }
        }
        if (surveysParsed){
            const surveys = Object.keys(surveysParsed).map((key) => {return surveysParsed[key];})
            setSurveys(surveys);
        }
        return;
      }
    }
    catch(error) {
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        console.log(error)
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get programs.'
          }));
        }
      return;
    }
    finally{
      setLoading(false);
    }
  }

  const closeAddProgramDialog = () => {
    setTrigger(false);
    setInitialData({});
    getData();
  }

  return (
    <section>
    <Card sx={{ margin: "auto" }}>
      <CardContent>
        <Typography sx={{ fontWeight: 600, textAlign: { xs: 'left', md: 'left' } }}
            variant="h6" component="div" gutterBottom>
          Survey List
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => openAddProgram()}
            disabled={!hasPermission('write', "program")}
          >
            Add survey
          </Button>
        </Box>

        {loading && (
          <Box sx={{ width: '100%', p: 2 }}>
            <LinearProgress />
          </Box>
        )}
        <TableContainer 
          elevation={0} 
          sx={{ 
            maxHeight: 600,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'auto'
          }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 50 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ProgramIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600"></Typography>
              </Stack>
            </TableCell>

            <TableCell sx={{ minWidth: 90 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ProgramIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Title</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ProgramIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Description</Typography>
              </Stack>
            </TableCell>
            <TableCell align="center" sx={{ width: 120 }}>
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                <ActionsIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Order by</Typography>
              </Stack>
            </TableCell>
            <TableCell align="center" sx={{ width: 120 }}>
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                <ActionsIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Active</Typography>
              </Stack>
            </TableCell>
            <TableCell align="center" sx={{ width: 120 }}>
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                <ActionsIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {surveys && surveys.map((s) => (
            <TableRow 
              key={s.id}
              hover
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: 'primary.secondary',
                      fontSize: '0.875rem'
                    }}
                  >
                    {s.title.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" fontWeight="500">
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight="500">
                    {s.title}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight="500">
                    {s.description}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight="500">
                    {s.order_by}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight="500">
                  {s.is_active ? <CheckBox color="success"/> : <IndeterminateCheckBoxRounded color="error"/>}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="Edit Program">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleUpdate(s)}
                      disabled={!hasPermission('write', 'program')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Program">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => confirmHandleDelete(s.id)}
                      disabled={!hasPermission('delete', 'program')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
        </TableContainer>
      </CardContent>
    </Card>
    <DeleteConfirmationDialog open={confirmDelete.open} onClose={closeConfirmHandleDelete} onConfirm={handleDelete} />
    <AddSurvey onClose={closeAddProgramDialog} initialData={initialData} open={trigger} />
    </section>
  );
};

export default SurveyList;
